/**
 * D431 (UI step 3): the pieces the Almanac, Festival and Region tabs share: a section head with
 * a mono count, the card, a figure, and a tile for an icon. Sizes follow the handoff's
 * sections 3 to 5.
 */
import type { ComponentChildren, JSX, RefObject } from 'preact';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { Icon } from '../ui/Icon.tsx';
import { idleBadges, type IdleBadges } from './badges.ts';
import { idleContent, useIdle } from './store.ts';

/** D460: every tab's badge for the open world, read from one place so every level agrees. */
export function useBadges(): IdleBadges | null {
  const world = useIdle((s) => s.world);
  const t = useIdle((s) => s.now);
  const seen = useIdle((s) => s.heritageSeen);
  const news = useIdle((s) => s.newSeen);
  return useMemo(
    () => (world ? idleBadges(world, idleContent, t, seen, news) : null),
    [world, t, seen, news],
  );
}

/** D460: a count carried down from the tab: 18px after a segment's label, 22px before a chevron. */
export function CountPill({
  n,
  size = 18,
  testId,
}: {
  n: number;
  size?: 18 | 22;
  testId?: string;
}) {
  return (
    <span
      class="num"
      data-testid={testId}
      style={{
        display: 'inline-block',
        minWidth: size,
        height: size,
        padding: size === 18 ? '0 5px' : '0 6px',
        borderRadius: 'var(--radius-round)',
        background: 'var(--accent)',
        color: 'var(--accent-ink)',
        font: `700 ${size === 18 ? 'var(--fs-badge)' : 'var(--fs-note)'}/${size}px var(--font-num)`,
        textAlign: 'center',
        boxSizing: 'border-box',
        flex: 'none',
      }}
    >
      {n}
    </span>
  );
}

/** D461: set by the tab bar, so only a tab opened from it arrives (a prompt has its own place). */
let arrivingByTab = false;

/** The tab bar is opening a tab: the tab, if badged, goes to its first marked thing. */
export function arriveByTab(): void {
  arrivingByTab = true;
}

/** Whether this tab was opened from the tab bar, read once when the tab opens. */
export function useArrival(): boolean {
  const [arrived] = useState(() => {
    const a = arrivingByTab;
    arrivingByTab = false;
    return a;
  });
  return arrived;
}

/**
 * D460: arriving on a badged tab, the first marked thing in `root` is scrolled into view and its
 * ring pulses once (300ms; the CSS drops it under reduced motion). Runs once, when the tab opens.
 */
export function useArrive(root: RefObject<HTMLElement>, badged: boolean): void {
  useEffect(() => {
    if (!badged) return;
    const id = requestAnimationFrame(() => {
      const el = root.current?.querySelector<HTMLElement>('.idle-waiting');
      if (!el) return;
      el.scrollIntoView?.({ block: 'nearest' });
      el.classList.add('idle-waiting-pulse');
      el.addEventListener('animationend', () => el.classList.remove('idle-waiting-pulse'), {
        once: true,
      });
    });
    return () => cancelAnimationFrame(id);
    // Once, on opening the tab.
  }, []);
}

/** A card: the surface, a hairline and 10px of padding. */
export const CARD10: JSX.CSSProperties = {
  display: 'grid',
  gap: 8,
  padding: 10,
  borderRadius: 'var(--radius-md)',
  background: 'var(--surface)',
  border: '1px solid var(--line)',
};

/** A tab's scrolling body: 12px gutters (D459), sections 8px apart. */
export const TAB_BODY: JSX.CSSProperties = {
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  display: 'grid',
  gap: 8,
  padding: '8px var(--edge) 12px',
  alignContent: 'start',
};

/** The 12px quiet line under a card or a heading. */
export const NOTE: JSX.CSSProperties = {
  font: '400 12px/1.35 var(--font-ui)',
  color: 'var(--ink-2)',
};

/** A section head: 12px 600 in `ink-2`, with an optional mono count on the right. */
export function SectionHead({
  title,
  count,
  testId,
  countTestId,
}: {
  title: ComponentChildren;
  count?: ComponentChildren;
  testId?: string;
  countTestId?: string;
}) {
  return (
    <div
      data-testid={testId}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        margin: '6px 2px 0',
        font: '600 12px/1.2 var(--font-ui)',
        color: 'var(--ink-2)',
      }}
    >
      <h3 style={{ margin: 0, font: 'inherit' }}>{title}</h3>
      {count !== undefined && (
        <span
          class="num"
          data-testid={countTestId}
          style={{ font: '500 12px/1.2 var(--font-num)', color: 'var(--ink-2)' }}
        >
          {count}
        </span>
      )}
    </div>
  );
}

/** A figure on a card: a 11px label, a 20px 700 mono value, and a note. */
export function Figure({
  label,
  value,
  note,
  color = 'var(--ink)',
  testId,
  valueTestId,
}: {
  label: ComponentChildren;
  value: ComponentChildren;
  note?: ComponentChildren;
  color?: string;
  testId?: string;
  valueTestId?: string;
}) {
  return (
    <div data-testid={testId} style={{ ...CARD10, gap: 0, alignContent: 'start' }}>
      <span style={{ font: '600 11px/1.2 var(--font-ui)', color: 'var(--ink-2)' }}>{label}</span>
      <span
        class="num"
        data-testid={valueTestId}
        style={{ font: '700 20px/1.2 var(--font-num)', color, marginTop: 4 }}
      >
        {value}
      </span>
      {note && <span style={{ ...NOTE, marginTop: 4 }}>{note}</span>}
    </div>
  );
}

/** A 48px tile in the inset ground with an icon, as on the producer rows. */
export function IconTile({
  name,
  size = 48,
  icon = 22,
}: {
  name: string;
  size?: number;
  icon?: number;
}) {
  return (
    <span
      aria-hidden="true"
      style={{
        flex: 'none',
        width: size,
        height: size,
        display: 'grid',
        placeItems: 'center',
        borderRadius: 'var(--radius-sm)',
        background: 'var(--surface-2)',
        color: 'var(--ink)',
      }}
    >
      <Icon name={name} size={icon} />
    </span>
  );
}

/** D457: the design system's lock, for a card that is not open yet. */
export function LockGlyph({ size = 18 }: { size?: number }) {
  return <Icon name="lock" size={size} />;
}

/**
 * D442: the upgrade book's two-way control (handoff section 4): an 8px control with 3px inset,
 * 44px segments of 6px radius and the count after each label in 11px mono. Each segment is a
 * tab; the selected one takes the `surface` fill.
 */
export function BookSwitch<T extends string>({
  options,
  value,
  onChange,
}: {
  /** `badge`, D460: the tab's count carried down to the segment that holds it, as an 18px pill. */
  options: { value: T; label: string; count?: number | string; badge?: number; testId?: string }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div
      role="tablist"
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))`,
        gap: 2,
        // D458: the standalone size, a 48px track with 44px segments.
        padding: 2,
        background: 'var(--surface-2)',
        borderRadius: 'var(--radius-md)',
      }}
    >
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="tab"
            aria-selected={on}
            data-testid={o.testId}
            onClick={() => onChange(o.value)}
            style={{
              height: 44,
              border: 0,
              borderRadius: 6,
              padding: '0 8px',
              background: on ? 'var(--surface)' : 'transparent',
              boxShadow: on ? 'inset 0 0 0 1px var(--line)' : 'none',
              color: 'var(--ink)',
              font: `${on ? 600 : 500} var(--fs-label)/1 var(--font-ui)`,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              whiteSpace: 'nowrap',
            }}
          >
            {o.label}
            {o.count !== undefined && (
              <span
                class="num"
                style={{ font: '500 var(--fs-note)/1 var(--font-num)', color: 'var(--ink)' }}
              >
                {o.count}
              </span>
            )}
            {o.badge ? (
              <CountPill n={o.badge} testId={o.testId ? `${o.testId}-badge` : undefined} />
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
